(function (global) {
  "use strict";

  var mounts = [];
  var bootAttempts = 0;
  var MAX_BOOT_ATTEMPTS = 40;
  var SHANGHAI_TZ = "Asia/Shanghai";
  var STORAGE_FORMAT = "YYYY-MM-DD HH:mm";
  var DISPLAY_FORMAT = "YYYY-MM-DD hh:mm A";

  function parseHiddenValue(raw) {
    if (!raw || !global.dayjs) return null;
    var parsed = global.dayjs(raw, STORAGE_FORMAT, true);
    return parsed.isValid() ? parsed : null;
  }

  function getShanghaiHourMinute(localDateTime) {
    var date = localDateTime && typeof localDateTime.toDate === "function"
      ? localDateTime.toDate()
      : new Date(localDateTime);
    var formatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: SHANGHAI_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    var hour = 0;
    var minute = 0;

    formatter.formatToParts(date).forEach(function (part) {
      if (part.type === "hour") hour = parseInt(part.value, 10);
      if (part.type === "minute") minute = parseInt(part.value, 10);
    });

    return { hour: hour, minute: minute };
  }

  function isSundayBlocked(localDateTime) {
    if (!localDateTime || !global.dayjs) return false;
    var dt = global.dayjs.isDayjs && global.dayjs.isDayjs(localDateTime)
      ? localDateTime
      : global.dayjs(localDateTime);
    if (!dt.isValid()) return false;

    var weekday = new Intl.DateTimeFormat("en-US", {
      timeZone: SHANGHAI_TZ,
      weekday: "short",
    }).format(dt.toDate());

    return weekday === "Sun";
  }

  function isSundayBlockedValue(raw) {
    return isSundayBlocked(parseHiddenValue(raw));
  }

  function isShanghaiQuietHoursBlocked(localDateTime) {
    if (!localDateTime || !global.dayjs) return false;
    var dt = global.dayjs.isDayjs && global.dayjs.isDayjs(localDateTime)
      ? localDateTime
      : global.dayjs(localDateTime);
    if (!dt.isValid()) return false;

    var shanghai = getShanghaiHourMinute(dt);
    var hour = shanghai.hour;

    // Gray out 01:00 through 08:59 Shanghai time.
    if (hour >= 1 && hour <= 8) return true;

    return false;
  }

  function isShanghaiQuietHoursBlockedValue(raw) {
    return isShanghaiQuietHoursBlocked(parseHiddenValue(raw));
  }

  function buildDisabledTime(date) {
    if (!date || !global.dayjs) return {};

    var base = global.dayjs(date);

    return {
      disabledHours: function () {
        var disabled = [];

        for (var hour = 0; hour < 24; hour += 1) {
          var hasValidMinute = false;

          for (var minute = 0; minute < 60; minute += 1) {
            var candidate = base.hour(hour).minute(minute).second(0).millisecond(0);
            if (!isShanghaiQuietHoursBlocked(candidate)) {
              hasValidMinute = true;
              break;
            }
          }

          if (!hasValidMinute) disabled.push(hour);
        }

        return disabled;
      },
      disabledMinutes: function (selectedHour) {
        var disabled = [];

        for (var minute = 0; minute < 60; minute += 1) {
          var candidate = base.hour(selectedHour).minute(minute).second(0).millisecond(0);
          if (isShanghaiQuietHoursBlocked(candidate)) disabled.push(minute);
        }

        return disabled;
      },
    };
  }

  function disabledDate(current) {
    if (!current || !global.dayjs) return false;
    if (current < global.dayjs().startOf("day")) return true;
    return isSundayBlocked(current);
  }

  function dependenciesReady() {
    return Boolean(global.Vue && global.antd && global.antd.DatePicker && global.dayjs);
  }

  function mountPicker(form, mountEl, hidden) {
    var Vue = global.Vue;
    var antd = global.antd;
    var dayjs = global.dayjs;
    var h = Vue.h;
    var name = mountEl.getAttribute("data-name");
    var placeholder = mountEl.getAttribute("data-placeholder") || "Select date and time";
    var locale = antd.enUS || (antd.locale && antd.locale.enUS);
    var valueRef = Vue.ref(parseHiddenValue(hidden.value));

    var app = Vue.createApp({
      setup: function () {
        Vue.watch(valueRef, function (next) {
          hidden.value = next ? dayjs(next).format(STORAGE_FORMAT) : "";
        });

        return function () {
          var picker = h(antd.DatePicker, {
            value: valueRef.value,
            "onUpdate:value": function (next) {
              valueRef.value = next;
            },
            format: DISPLAY_FORMAT,
            hideDisabledOptions: false,
            showTime: {
              format: "hh:mm A",
              use12Hours: true,
              minuteStep: 1,
              defaultValue: dayjs("00:00:00", "HH:mm:ss"),
              hideDisabledOptions: false,
            },
            disabledDate: disabledDate,
            disabledTime: function (current) {
              return buildDisabledTime(current);
            },
            placeholder: placeholder,
            dropdownClassName: "beyke-inquiry-picker",
            getPopupContainer: function () {
              return document.getElementById("contact-form") || mountEl;
            },
            inputReadOnly: true,
            style: { width: "100%" },
          });

          if (!locale) return picker;

          return h(antd.ConfigProvider, { locale: locale, theme: { primaryColor: "#6f9327" } }, function () {
            return picker;
          });
        };
      },
    });

    app.use(antd);
    app.mount(mountEl);

    mounts.push({
      name: name,
        reset: function () {
          try {
            valueRef.value = null;
            hidden.value = "";
          } catch (error) {
            hidden.value = "";
          }
        },
      focus: function () {
        var input = mountEl.querySelector(".ant-picker-input input");
        if (input) input.focus();
      },
      hidden: hidden,
    });
  }

  function initContactDatePickers(form) {
    if (!form) return false;
    if (!dependenciesReady()) return false;

    form.querySelectorAll(".beyke-ant-datetime-field").forEach(function (mountEl) {
      var name = mountEl.getAttribute("data-name");
      if (!name || mountEl.__beykeDatePickerMounted) return;

      var hidden = form.querySelector('input[type="hidden"][name="' + name + '"]');
      if (!hidden) return;

      mountEl.__beykeDatePickerMounted = true;
      mountPicker(form, mountEl, hidden);
    });

    return mounts.length > 0;
  }

  function resetContactDatePickers() {
    mounts.forEach(function (entry) {
      entry.reset();
    });
  }

  function focusContactDatePicker(name) {
    mounts.forEach(function (entry) {
      if (entry.name === name) entry.focus();
    });
  }

  global.BeykeContactDatePickers = {
    reset: resetContactDatePickers,
    focus: focusContactDatePicker,
    isShanghaiQuietHoursBlocked: isShanghaiQuietHoursBlockedValue,
    isSundayBlocked: isSundayBlockedValue,
  };

  function boot() {
    var form = document.getElementById("beyke-contact-form");
    if (initContactDatePickers(form)) return;

    bootAttempts += 1;
    if (bootAttempts < MAX_BOOT_ATTEMPTS) {
      global.setTimeout(boot, 100);
      return;
    }

    if (global.console && typeof global.console.error === "function") {
      global.console.error(
        "[BeykeContactDatePickers] Failed to mount date pickers. Missing:",
        {
          Vue: Boolean(global.Vue),
          antd: Boolean(global.antd),
          DatePicker: Boolean(global.antd && global.antd.DatePicker),
          dayjs: Boolean(global.dayjs),
        }
      );
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else {
    boot();
  }
})(window);
